import { ApiError } from '../../api/client';

/**
 * Refus de publication lisible : le serveur rend la commande invalide avec le détail du contrat (`field_errors`,
 * chemins JSON et messages du validateur). L'éditeur ne montre ni chemin ni identifiant technique (éditeurs sans
 * JSON) : on garde l'erreur la plus précise, on nomme la section ou le champ par son libellé et chaque réglage par
 * un mot, et on dit ce qui ne va pas en trois mots.
 */

type Json = Record<string, unknown>;

const WORDS: Readonly<Record<string, string>> = {
  layout: 'mise en page',
  entry_panel: 'panneau de saisie',
  pending_row: 'ligne en cours',
  next_button: 'bouton du bas',
  presentation: 'présentation',
  options: 'choix',
  image: 'image',
  icon: 'icône',
  header: 'en-tête',
  footer: 'pied',
  aggregates: 'totaux',
  pages: 'pages',
  validation: 'contrôles',
  label: 'libellé',
  description: 'description',
  visual: 'visuel',
  confirmation: 'confirmation',
  success: 'fin de saisie',
};

const fr = (label: unknown): string | undefined => {
  if (typeof label === 'string') return label;
  if (label !== null && typeof label === 'object') {
    const texts = label as Record<string, unknown>;
    const found = texts['fr'] ?? Object.values(texts)[0];
    return typeof found === 'string' ? found : undefined;
  }
  return undefined;
};

const sectionsOf = (draft: Json): Json[] => (Array.isArray(draft['sections']) ? (draft['sections'] as Json[]) : []);

/** Les étapes lisibles d'un chemin du corps du gabarit ; un segment sans nom lisible est tu. */
export function trailOf(path: readonly string[], draft: Json): string[] {
  const trail: string[] = [];
  const sections = sectionsOf(draft);
  for (let i = 0; i < path.length; i += 1) {
    const segment = path[i]!;
    if (segment === 'placements' && path[i + 1] !== undefined) {
      const section = sections.find((row) => row['section_id'] === path[i + 1]);
      const name = fr(section?.['label']);
      if (name !== undefined) trail.unshift(`« ${name} »`);
      i += 1;
      continue;
    }
    if (segment === 'sections' && path[i + 1] !== undefined) {
      const section = sections[Number(path[i + 1])];
      const name = fr(section?.['label']);
      if (name !== undefined) trail.push(`« ${name} »`);
      i += 1;
      if (path[i + 1] === 'fields' && path[i + 2] !== undefined) {
        const fields = Array.isArray(section?.['fields']) ? (section['fields'] as Json[]) : [];
        const entry = fields[Number(path[i + 2])];
        const field = (entry?.['field'] ?? entry) as Json | undefined;
        const fieldName = fr(field?.['label']);
        if (fieldName !== undefined) trail.push(`« ${fieldName} »`);
        i += 2;
      }
      continue;
    }
    const word = WORDS[segment];
    if (word !== undefined && trail[trail.length - 1] !== word) trail.push(word);
  }
  return trail;
}

const problemOf = (message: string): string =>
  message.startsWith('additionalProperties') ? 'réglage inconnu de cette version' : message.startsWith('required') ? 'réglage obligatoire manquant' : 'valeur refusée';

/**
 * L'erreur à montrer pour une publication refusée : une `Error` lisible quand le serveur a détaillé un refus du
 * contrat du gabarit, l'erreur d'origine sinon (un constat du serveur, `TEMPLATE_DRAFT_INVALID`, est déjà rédigé).
 */
export function publishRefusal(failure: unknown, draft: object): unknown {
  if (!(failure instanceof ApiError)) return failure;
  // Éditeur resté ouvert sur le gabarit du Pack alors que le client a publié sa version entre-temps.
  if (failure.code === 'TEMPLATE_OVERRIDDEN') return new Error('Le client a déjà sa version de ce formulaire : rechargez la page, ouvrez « la version du client », puis refaites la modification.');
  const body = failure.fieldErrors
    .map((error) => ({ error, path: error.field.split('.') }))
    .map(({ error, path }) => ({ error, path: path.slice(path.indexOf('body') + 1), inBody: path.includes('body') }))
    .filter(({ error, inBody, path }) => error.code === 'PAYLOAD_CONTRACT_VIOLATION' && inBody && path.length > 0);
  if (body.length === 0) return failure;
  // Les autres branches d'un `oneOf` remontent aussi, au niveau du corps : la plus profonde est la vraie.
  const depth = Math.max(...body.map(({ path }) => path.length));
  const deepest = body.filter(({ path }) => path.length === depth)[0]!;
  const trail = trailOf(deepest.path, draft as Json);
  const where = trail.length === 0 ? '' : ` — ${trail.join(' › ')}`;
  return new Error(`Publication refusée : ${problemOf(deepest.error.message)}${where}.`);
}
